/**
 * Everyone who ever turned the feature on, one page at a time.
 *
 * The first column is the platform user id and nothing else. There is no name and no e-mail here
 * by design: the BFF has no platform-user table, and resolving one would mean copying personal
 * data for a screen that does not need it. Do not put a placeholder name in its place — a made-up
 * name is worse than an id, because it reads as if it were true.
 *
 * There is no "Motivo" column any more: `lastReason` stopped existing with BFF 5.0.0. Why somebody
 * left is a survey answer now, and it is read on the results screen of the exit questionnaire —
 * per question, in the words they were asked.
 *
 * @module Pages/BUs/OptInFeatures
 */

import { css } from '@emotion/css';
import type { JSX } from 'react';
import type { OptInFeatureUser } from '../../../Types/OptInFeatures.types';
import { formatEventDate } from '../../../Utils/adoptionFormat';
import { IconButton } from '@/Shared/components/IconButton/IconButton';
import { StatusTag } from '@/Shared/components/StatusTag/StatusTag';

const scrollStyle = css`
  overflow-x: auto;
`;

const tableStyle = css`
  width: 100%;
  border-collapse: collapse;
  font-size: var(--font-size-sm);
  min-width: 640px;
`;

const headStyle = css`
  text-align: left;
  padding: var(--space-s) var(--space-m);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-secondary);
  border-bottom: 1px solid var(--color-border);
  white-space: nowrap;
`;

const cellStyle = css`
  padding: var(--space-s) var(--space-m);
  border-bottom: 1px solid var(--color-divider);
  white-space: nowrap;
`;

const idCellStyle = css`
  font-family: var(--font-mono);
  font-size: var(--font-size-xs);
`;

const emptyStyle = css`
  font-size: var(--font-size-sm);
  color: var(--color-text-muted);
`;

const actionCellStyle = css`
  padding: 4px var(--space-m);
  border-bottom: 1px solid var(--color-divider);
  width: 1%;
  text-align: right;
`;

export interface AdoptionUsersTableProps {
  users: OptInFeatureUser[];
  id?: string;
  /**
   * Erasing one person's opt-in data. Absent when it is not on offer, and then the whole column is
   * **not rendered** — a disabled button would still advertise an action nobody here can take.
   */
  onErase?: (user: OptInFeatureUser) => void;
  isErasing?: boolean;
}

export const AdoptionUsersTable = ({
  users,
  id,
  onErase,
  isErasing = false,
}: AdoptionUsersTableProps): JSX.Element => {
  if (users.length === 0) {
    return (
      <p id={id} className={emptyStyle}>
        Ninguém ligou esta funcionalidade ainda.
      </p>
    );
  }

  return (
    <div className={scrollStyle}>
      <table id={id} className={tableStyle}>
        <thead>
          <tr>
            <th className={headStyle}>Platform user</th>
            <th className={headStyle}>Estado</th>
            <th className={headStyle}>Primeira ativação</th>
            <th className={headStyle}>Última ativação</th>
            <th className={headStyle}>Desativou em</th>
            {onErase ? <th className={headStyle}>Ação</th> : null}
          </tr>
        </thead>
        <tbody>
          {users.map(user => (
            <tr key={user.platformUserId} id={`opt-in-adoption-user-${user.platformUserId}`}>
              <td className={`${cellStyle} ${idCellStyle}`}>{user.platformUserId}</td>
              <td className={cellStyle}>
                <StatusTag status={user.isEnabled ? 'success' : 'neutral'} size='sm' dot>
                  {user.isEnabled ? 'Ligada' : 'Desligada'}
                </StatusTag>
              </td>
              <td className={cellStyle}>{formatEventDate(user.firstEnabledAt)}</td>
              <td className={cellStyle}>{formatEventDate(user.lastEnabledAt)}</td>
              <td className={cellStyle}>{formatEventDate(user.disabledAt)}</td>
              {onErase ? (
                <td className={actionCellStyle}>
                  <IconButton
                    id={`opt-in-adoption-user-erase-${user.platformUserId}`}
                    icon='Misc.Trash'
                    variant='danger'
                    size='sm'
                    disabled={isErasing}
                    aria-label={`Apagar os dados de opt-in da pessoa ${user.platformUserId}`}
                    onClick={() => {
                      onErase(user);
                    }}
                  />
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
